import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, Typography, styled } from '@mui/material';
import { debounce } from 'lodash';
import UserService from '../../services/userService';

interface CarrierOption {
  id: string;
  name: string;
  category: string;
  states: string[];
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
	topLabel?: boolean;
}

// Create a styled version of TextField to handle custom styles
const StyledTextField = styled(TextField)<{ customStyle?: React.CSSProperties }>(({ customStyle }) => ({
  '& .MuiFormControl-root': {
    ...customStyle
  },
  '& .MuiInputBase-root': {
    ...customStyle,
    height: customStyle?.height,
    backgroundColor: customStyle?.backgroundColor,
    borderRadius: customStyle?.borderRadius,
    border: customStyle?.border,
    padding: customStyle?.padding,
		justifyContent: customStyle?.justifyContent,
		textAlign: customStyle?.textAlign,
    paddingRight: '0px !important'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
	'input': {
		textAlign: customStyle?.textAlign,
	}
}));

const CarrierAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  label = 'Insurance Carrier',
  placeholder = 'Search for your carrier...',
  error = false,
  helperText = '',
  className = '',
  required = false,
  disabled = false,
  topLabel = false,
  style = {},
}) => {
  const [options, setOptions] = useState<CarrierOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasFetchedAll, setHasFetchedAll] = useState(false); // NEW

  const fetchCarriers = debounce(async (searchText: string) => {
    setLoading(true);
    try {
      const response = await UserService.getCarriers({ name: searchText });
      const sortedOptions = (response.data || []).sort((a: any, b: any) => {
        const search = searchText.toLowerCase();
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
  
        const aStarts = aName.startsWith(search);
        const bStarts = bName.startsWith(search);
  
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
  
        return aName.localeCompare(bName); // fallback A-Z
      });
      setOptions(sortedOptions);
    } catch (error) {
      console.error('Error fetching carriers:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, 300);
  
  const fetchAllCarriers = async () => {
    if (hasFetchedAll) return;
    setLoading(true);
    try {
      const response = await UserService.getCarriers({}); // fetch all
      const sorted = (response.data || []).sort((a: any, b: any) =>
        a.name.localeCompare(b.name) // alphabetical A-Z
      );
      setOptions(sorted);
      setHasFetchedAll(true);
    } catch (error) {
      console.error('Error fetching all carriers:', error);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    if (value && !options.length) {
      fetchCarriers(value);
    }
  }, [value]);

  const selectedCarrier = options?.find(option => option.name === value) || null;

  return (
    <>
      {topLabel && (
        <Typography variant="body1" sx={{ fontSize: '16px !important' }}>
          {label}
        </Typography>
      )}
      <Autocomplete
        style={{ width: '100%' }}
        value={selectedCarrier}
        onChange={(_, newValue: CarrierOption | null) => {
          onChange(newValue?.name || '');
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
          fetchCarriers(newInputValue);
        }}
        onFocus={fetchAllCarriers}
        options={options}
        getOptionLabel={(option: CarrierOption | string) =>
          typeof option === 'string' ? option : option.name
        }
        isOptionEqualToValue={(option: CarrierOption, value: CarrierOption) =>
          option.name === value.name
        }
        loading={loading}
        className={className}
        disabled={disabled}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            sx={{ mb: 1, mt: 0.5 }}
            customStyle={style}
            label={topLabel ? '' : label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            required={required}
            fullWidth
            inputProps={{
              ...params.inputProps,
              style: { padding: 0, color: '#000', fontSize: '14px' },
            }}
          />
        )}
      />
    </>
  );
};

export default CarrierAutocomplete;